import { AlertTriangle } from "lucide-react"

/**
 * "These numbers are real but old" — the honest middle state between live
 * numbers and a blank card.
 *
 * When PostHog stops answering, the backend serves the last good answer it got
 * for that same question and reports how old it is. Rendering that silently
 * would be the same class of lie as the false zeros this whole fix exists to
 * kill, so a card that accepts aged numbers MUST show their age. Pass the
 * endpoint's `stale_as_of` straight in; null renders nothing, so this is safe
 * to leave mounted unconditionally.
 */
export function StaleBanner({
  asOf,
  children,
}: {
  asOf?: string | null
  /**
   * Card-specific reassurance, appended after the warning — e.g. which half of
   * a mixed DB/PostHog card is still current. Cards that are PostHog all the
   * way down pass nothing.
   */
  children?: React.ReactNode
}) {
  if (!asOf) return null
  return (
    <p className="text-base text-muted-foreground max-w-5xl">
      <AlertTriangle className="inline size-4 mr-1 -mt-0.5 text-amber-600" />
      <strong className="text-foreground">
        PostHog isn't answering — showing the last numbers it gave us, from{" "}
        {new Date(asOf).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
        .
      </strong>{" "}
      {children} Refresh in a few minutes for live event numbers.
    </p>
  )
}
